import { ComponentProps } from 'react'

import { DetailsItemsList } from '@factorialco/f0-react/dist/experimental'
import { ProfileOverviewQuery } from 'generated/resources/graphql'

import { useCurrentAccessWeekdays } from 'modules/core/hooks/useCurrentAccessWeekdays'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import buildUrl from 'modules/core/lib/buildUrl'
import { getUIDuration, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { getCountryField } from 'modules/contracts/lib/getCountryField'
import { asShortDay } from 'modules/contracts/lib/shortDays'

interface DetailsSidebarProps {
  data: ProfileOverviewQuery
}

export const DetailsSidebar: React.FC<DetailsSidebarProps> = ({ data }) => {
  const accessWeekdays = useCurrentAccessWeekdays()
  const { navigation } = useNavigation()

  const employeeData = data.employees.employee
  const contractData = data.contracts.referenceContractsConnection.nodes[0]
  const isVersionDataEnabled = data.features.featuresConnection.nodes.some(
    (feature) => feature.name === 'dev_contracts_data_as_json' && feature.enabled
  )
  const contractTypeName =
    getCountryField({
      isVersionDataEnabled,
      contractVersion: contractData,
      field: 'contract_type',
    })?.name ?? null

  const indexByDay = new Map(
    Array.from(accessWeekdays.entries()).map(([index, day]) => [day, index])
  )
  const workingWeekdays = contractData?.workingWeekDays?.split(',') ?? []
  const workingWeekdaysIndices = workingWeekdays
    .map((day) => indexByDay.get(day))
    .filter((el) => el !== undefined)
  const daysOfTheWeek = accessWeekdays.map((day) => asShortDay(day) || day)
  const managerId = employeeData?.manager?.id
  const managerFirstName =
    employeeData?.manager?.preferredName || employeeData?.manager?.access.firstName
  const managerLastName = employeeData?.manager?.access.lastName
  const managerAvatar = employeeData?.manager?.access?.avatar ?? ''

  const teams =
    employeeData?.memberships.map(({ team }) => ({
      name: team?.name ?? '',
      type: 'team' as const,
      action: {
        type: 'navigate' as const,
        href: navigation.employees.teams.id.fullPath({
          id: team?.id ?? '0',
        }),
      },
    })) ?? []

  const details = useUnsafeMemo(() => {
    const items: ComponentProps<typeof DetailsItemsList>['details'] = []

    if (managerFirstName && managerLastName) {
      items.push({
        title: i18n.t('employees.profile.overview.manager'),
        content: {
          type: 'person',
          firstName: managerFirstName,
          lastName: managerLastName,
          avatarUrl: buildUrl(managerAvatar),
          action: managerId
            ? {
                type: 'navigate',
                href: navigation.employees.id.fullPath({
                  id: managerId,
                }),
              }
            : undefined,
        },
      })
    }

    if (employeeData?.loginEmail) {
      items.push({
        title: i18n.t('employees.profile.overview.email'),
        content: {
          type: 'item',
          text: employeeData.loginEmail,
          action: {
            type: 'copy',
          },
        },
      })
    }

    if (employeeData?.phoneNumber) {
      items.push({
        title: i18n.t('employees.profile.overview.phone'),
        content: {
          type: 'item',
          text: employeeData.phoneNumber,
          action: {
            type: 'copy',
          },
        },
      })
    }

    const legalEntityName = employeeData?.legalEntity?.legalName

    if (legalEntityName) {
      items.push({
        title: i18n.t('employees.profile.overview.legal_entity'),
        content: {
          type: 'item',
          text: legalEntityName,
          action: {
            type: 'copy',
          },
        },
      })
    }

    if (contractTypeName) {
      items.push({
        title: i18n.t('employees.profile.overview.contract_type'),
        content: {
          type: 'item',
          text: contractTypeName,
        },
      })
    }

    if (contractData?.startsOn) {
      const date = stringToDate(contractData.startsOn, 'backend')
      const duration = getUIDuration({ date, withPrefixOrSuffix: true })

      items.push({
        title: i18n.t('employees.profile.overview.start_date'),
        content: {
          type: 'item',
          text: `${date.format('LL')} (${duration})`,
        },
      })
    }

    if (contractData?.endsOn) {
      const date = stringToDate(contractData.endsOn, 'backend')
      const duration = getUIDuration({ date, withPrefixOrSuffix: true })

      items.push({
        title: i18n.t('employees.profile.overview.end_date'),
        content: {
          type: 'item',
          text: `${date.format('LL')} (${duration})`,
        },
      })
    }

    items.push({
      title: i18n.t('employees.profile.overview.workdays'),
      content: {
        type: 'weekdays',
        activatedDays: workingWeekdaysIndices,
        daysOfTheWeek,
      },
    })

    if (teams.length) {
      items.push({
        title: i18n.t('employees.profile.overview.teams'),
        content: teams,
      })
    }

    return items
  }, [
    managerFirstName,
    managerLastName,
    managerAvatar,
    managerId,
    employeeData,
    contractTypeName,
    contractData,
    teams,
  ])

  return <DetailsItemsList title={i18n.t('employees.profile.overview.title')} details={details} />
}
